import { ReactNode, useCallback, useMemo, useState } from 'react';

import {
  ACCEPT_TYPE_IMAGE,
  AcceptTypes,
} from 'lib/form/file-dropper/file-dropper.constants';
import { FileDropperImage } from 'lib/form/file-dropper/file-dropper-image';
import { useFileDropperContext } from 'lib/form/file-dropper/file-dropper.context';
import { useImageLoad } from 'approot/shared/images/images.hooks';
import ImagePreviewStatic from 'lib/form/image-picker/image-preview-static';
import { ImageUploadWrapper } from './image-upload-field.style';

export const IMAGE_FILE_KEY_FIELD = 'imageKey';

export type ImageProps = {
  key: string;
  designId?: string | null;
  flip?: string | null;
  fileName: string;
  original?: string | null;
  alt?: string | null;
  file?: File | undefined;
} | null;

export type ImageUploadFunction = (
  key: string,
  fileName: string,
  file: File | undefined
) => void;

export type Props = {
  image?: ImageProps;
  fieldName?: string;
  accept?: AcceptTypes;
  elementDimensionLabel?: string;
  extraActions?: ReactNode;
  error?: string;
  showAcceptExtensions?: boolean;
  onImageUpload?: ImageUploadFunction;
  onDelete?: () => void;
};

const ImageUploadField = ({
  image,
  fieldName,
  accept,
  elementDimensionLabel,
  extraActions,
  error,
  showAcceptExtensions,
  onImageUpload,
  onDelete,
}: Props) => {
  const [isShowFileDropper, setIsShowFileDropper] = useState<boolean>(!image);

  const { clearFile } = useFileDropperContext(
    fieldName || IMAGE_FILE_KEY_FIELD
  );

  const { imageHasLoaded } = useImageLoad(image?.original || undefined, () => {
    setIsShowFileDropper(false);
  });

  const handleFileChange = useCallback(
    (key: string, fileName: string, file: File | undefined) => {
      onImageUpload?.(key, fileName, file);
    },
    [onImageUpload]
  );

  const handleDeleteImage = useCallback(() => {
    setIsShowFileDropper(true);
    clearFile();
    onDelete?.();
  }, [setIsShowFileDropper, clearFile, onDelete]);

  const isDisplayImage = !isShowFileDropper;

  const imageUrl = useMemo(() => {
    return image?.file
      ? URL.createObjectURL(image.file)
      : image?.original || '';
  }, [image]);

  return (
    <>
      {isShowFileDropper ? (
        <FileDropperImage
          name={fieldName || IMAGE_FILE_KEY_FIELD}
          accept={accept ? accept : [ACCEPT_TYPE_IMAGE]}
          error={error}
          isFinishing={!!(image?.original && !imageHasLoaded)}
          additionalLabel={elementDimensionLabel}
          showAcceptExtensions={showAcceptExtensions}
          onChange={handleFileChange}
        />
      ) : (
        <div>
          {isDisplayImage && image && (
            <ImageUploadWrapper>
              <ImagePreviewStatic
                value={{ key: image.key }}
                onChange={() => {}}
                url={imageUrl}
                deleteImage={handleDeleteImage}
                s3Key={image.key}
                extraActions={extraActions}
              />
            </ImageUploadWrapper>
          )}
        </div>
      )}
    </>
  );
};

export default ImageUploadField;
